import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  api,
  type ReceiptJobBatchResultItem,
  type ReceiptJobCreateData,
  type ReceiptJobStatusData,
} from "../lib/api";
import { AppError, logError, toUserMessage } from "../lib/errors";
import {
  RECEIPT_ACCEPTED_IMAGE_TYPES,
  RECEIPT_BATCH_MAX_FILES,
  clearPendingReceiptJobSnapshot,
  compressReceiptImage,
  formatReceiptStageLabel,
  getReceiptPollIntervalMs,
  getReceiptFileValidationMessage,
  type PendingReceiptJobNotification,
  readPendingReceiptJobSnapshot,
  writePendingReceiptJobNotification,
  writePendingReceiptJobSnapshot,
  writePendingReceiptNeedsReviewNotification,
} from "../lib/receipt";

type ReceiptJobViewStatus = "idle" | "uploading" | "accepted" | "polling" | "completed" | "failed" | "timeout";

type ReceiptJobView = {
  status: ReceiptJobViewStatus;
  jobId?: string;
  clientRequestId?: string;
  acceptedAt?: string;
  processedAt?: string | null;
  extractedSummary?: string | null;
  failedStage?: string | null;
  failedError?: string | null;
};

type ReceiptCaptureCardProps = {
  returnTo: string;
  conversationId?: string;
  messageId?: string;
  onJobResolved?: (notification: PendingReceiptJobNotification) => void;
};

const PICK_IMAGE_BUTTON_LABEL = "画像を選択";
const SUBMIT_RECEIPT_BUTTON_LABEL = "依頼する";
const REFRESH_JOB_STATUS_BUTTON_LABEL = "状態を再確認";

function formatDateTime(value?: string | null) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getBackendMessage(error: unknown) {
  if (!(error instanceof AppError)) return undefined;

  const responseText = error.meta.responseText?.trim();
  if (!responseText) return undefined;

  try {
    const parsed = JSON.parse(responseText);

    if (typeof parsed?.message === "string" && parsed.message.trim()) {
      return parsed.message.trim();
    }

    if (typeof parsed?.detail?.message === "string" && parsed.detail.message.trim()) {
      return parsed.detail.message.trim();
    }

    if (Array.isArray(parsed?.errors?.file) && typeof parsed.errors.file[0] === "string") {
      return parsed.errors.file[0];
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function isReceiptJobNotFoundError(error: unknown) {
  return error instanceof AppError && error.code === "NOT_FOUND";
}

const RECEIPT_GUIDE_LINES = ["画像を選んで依頼すると、領収書整理を開始します。"];

function getJobHeadline(job: ReceiptJobView, statusError: string | null) {
  if (statusError) return statusError;

  switch (job.status) {
    case "uploading":
      return "領収書の処理を開始しています";
    case "accepted":
    case "polling":
      return "領収書を処理しています";
    case "completed":
      return "領収書の処理が完了しました";
    case "failed":
      return "領収書の処理に失敗しました";
    case "timeout":
      return "処理の完了確認に時間がかかっています";
    default:
      return "";
  }
}

export function ReceiptCaptureCard({
  returnTo,
  conversationId,
  messageId,
  onJobResolved,
}: ReceiptCaptureCardProps) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pollTimerRef = useRef<number | null>(null);

  const [job, setJob] = useState<ReceiptJobView>({ status: "idle" });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedPreviews, setSelectedPreviews] = useState<{ name: string; url: string }[]>([]);
  const [batchResults, setBatchResults] = useState<ReceiptJobBatchResultItem[] | null>(null);
  const [screenError, setScreenError] = useState<string | null>(null);
  const [jobRefreshing, setJobRefreshing] = useState(false);

  const jobHeadline = useMemo(() => getJobHeadline(job, screenError), [job, screenError]);
  const isJobProcessing =
    job.status === "uploading" || job.status === "accepted" || job.status === "polling";

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const notifyJobResolved = useCallback(
    (response: ReceiptJobStatusData) => {
      if (response.status !== "completed" && response.status !== "failed") {
        return;
      }

      const notification: PendingReceiptJobNotification = {
        jobId: response.jobId,
        status: response.status,
        processedAt: response.processedAt ?? undefined,
        extractedSummary: response.extractedSummary ?? undefined,
        failedStage: response.failedStage ?? undefined,
        failedError: response.failedError ?? undefined,
      };

      writePendingReceiptJobNotification(notification);
      if (response.status === "completed" && response.needsReview) {
        writePendingReceiptNeedsReviewNotification(response.jobId);
      }
      onJobResolved?.(notification);
    },
    [onJobResolved]
  );

  const resetMissingReceiptJob = useCallback(() => {
    stopPolling();
    clearPendingReceiptJobSnapshot();
    setJob({ status: "idle" });
    setScreenError("前回の領収書ジョブが見つかりませんでした。もう一度依頼できます。");
  }, [stopPolling]);

  const updateJobFromStatus = useCallback(
    (response: ReceiptJobStatusData) => {
      const nextJob: ReceiptJobView = {
        status:
          response.status === "completed"
            ? "completed"
            : response.status === "failed"
              ? "failed"
              : "polling",
        jobId: response.jobId,
        clientRequestId: response.clientRequestId,
        processedAt: response.processedAt ?? null,
        extractedSummary: response.extractedSummary ?? null,
        failedStage: response.failedStage ?? null,
        failedError: response.failedError ?? null,
      };

      if (response.status === "completed" || response.status === "failed") {
        setScreenError(null);
        clearPendingReceiptJobSnapshot();
        stopPolling();
        notifyJobResolved(response);
      }

      setJob(nextJob);
      return nextJob;
    },
    [notifyJobResolved, stopPolling]
  );

  const scheduleJobPoll = useCallback(
    (jobId: string, startedAtMs: number, clientRequestId?: string) => {
      stopPolling();
      const pollIntervalMs = getReceiptPollIntervalMs(Math.max(0, Date.now() - startedAtMs));

      pollTimerRef.current = window.setTimeout(async () => {
        setJobRefreshing(true);
        try {
          const response = await api.getReceiptJob(jobId);
          const nextJob = updateJobFromStatus(response);

          if (response.status === "accepted") {
            setJob({
              ...nextJob,
              status: "polling",
            });
            scheduleJobPoll(jobId, startedAtMs, response.clientRequestId);
          }
        } catch (error) {
          if (isReceiptJobNotFoundError(error)) {
            resetMissingReceiptJob();
            return;
          }

          logError(error);
          setJob((prev) => ({
            ...prev,
            status: "polling",
            jobId,
            clientRequestId: clientRequestId ?? prev.clientRequestId,
          }));
          scheduleJobPoll(jobId, startedAtMs, clientRequestId);
        } finally {
          setJobRefreshing(false);
        }
      }, pollIntervalMs);
    },
    [resetMissingReceiptJob, stopPolling, updateJobFromStatus]
  );

  const beginPolling = useCallback(
    (response: ReceiptJobCreateData, startedAt: string) => {
      const startedAtMs = Date.parse(startedAt);
      const safeStartedAtMs = Number.isFinite(startedAtMs) ? startedAtMs : Date.now();

      writePendingReceiptJobSnapshot({
        jobId: response.jobId,
        clientRequestId: response.clientRequestId,
        startedAt: new Date(safeStartedAtMs).toISOString(),
        returnTo,
      });
      writePendingReceiptJobNotification({ jobId: response.jobId, status: "processing" });

      setJob({
        status: "accepted",
        jobId: response.jobId,
        clientRequestId: response.clientRequestId,
        acceptedAt: response.acceptedAt,
      });
      scheduleJobPoll(response.jobId, safeStartedAtMs, response.clientRequestId);
    },
    [returnTo, scheduleJobPoll]
  );

  const refreshPendingJob = useCallback(
    async (jobId: string, startedAt: string, clientRequestId?: string) => {
      const startedAtMs = Date.parse(startedAt);
      const safeStartedAtMs = Number.isFinite(startedAtMs) ? startedAtMs : Date.now();

      stopPolling();
      setJobRefreshing(true);
      setScreenError(null);

      try {
        const response = await api.getReceiptJob(jobId);
        const nextJob = updateJobFromStatus(response);

        if (response.status === "accepted") {
          setJob({
            ...nextJob,
            status: "polling",
          });
          scheduleJobPoll(jobId, safeStartedAtMs, response.clientRequestId ?? clientRequestId);
        }
      } catch (error) {
        if (isReceiptJobNotFoundError(error)) {
          resetMissingReceiptJob();
          return;
        }

        logError(error);
        setJob({
          status: "polling",
          jobId,
          clientRequestId,
        });
        scheduleJobPoll(jobId, safeStartedAtMs, clientRequestId);
      } finally {
        setJobRefreshing(false);
      }
    },
    [resetMissingReceiptJob, scheduleJobPoll, stopPolling, updateJobFromStatus]
  );

  useEffect(() => {
    const pendingSnapshot = readPendingReceiptJobSnapshot();
    if (pendingSnapshot?.jobId) {
      setJob({
        status: "polling",
        jobId: pendingSnapshot.jobId,
        clientRequestId: pendingSnapshot.clientRequestId,
      });
      void refreshPendingJob(
        pendingSnapshot.jobId,
        pendingSnapshot.startedAt,
        pendingSnapshot.clientRequestId
      );
    }

    return () => {
      stopPolling();
    };
  }, [refreshPendingJob, stopPolling]);

  useEffect(() => {
    const handleFocus = () => {
      const pendingSnapshot = readPendingReceiptJobSnapshot();
      if (pendingSnapshot?.jobId && job.status !== "uploading") {
        void refreshPendingJob(
          pendingSnapshot.jobId,
          pendingSnapshot.startedAt,
          pendingSnapshot.clientRequestId
        );
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleFocus();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [job.status, refreshPendingJob]);

  useEffect(() => {
    return () => {
      selectedPreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [selectedPreviews]);

  const clearSelectedReceipt = useCallback(() => {
    setSelectedFiles([]);
    setSelectedPreviews([]);
    setBatchResults(null);
  }, []);

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleRefreshJob = async () => {
    const jobId = job.jobId ?? readPendingReceiptJobSnapshot()?.jobId;
    const startedAt = readPendingReceiptJobSnapshot()?.startedAt ?? new Date().toISOString();
    const clientRequestId = job.clientRequestId ?? readPendingReceiptJobSnapshot()?.clientRequestId;

    if (!jobId) {
      setScreenError("確認対象のジョブが見つかりませんでした。");
      return;
    }

    await refreshPendingJob(jobId, startedAt, clientRequestId);
  };

  const handlePickAnotherFile = () => {
    setScreenError(null);
    setJob({ status: "idle" });
    clearSelectedReceipt();
    clearPendingReceiptJobSnapshot();
    openFilePicker();
  };

  const handleFileSelected: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";

    if (files.length === 0) return;

    if (files.length > RECEIPT_BATCH_MAX_FILES) {
      setScreenError(`一度に選択できる画像は${RECEIPT_BATCH_MAX_FILES}枚までです。`);
      return;
    }

    for (const file of files) {
      const validationMessage = getReceiptFileValidationMessage(file);
      if (validationMessage) {
        setScreenError(validationMessage);
        return;
      }
    }

    clearPendingReceiptJobSnapshot();
    setJob({ status: "idle" });
    setBatchResults(null);
    setSelectedFiles(files);
    setSelectedPreviews(files.map((file) => ({ name: file.name, url: URL.createObjectURL(file) })));
    setScreenError(null);
  };

  // 直近の成功分だけ、既存のバックグラウンド監視(JobNotificationCenter)に引き継ぐ。
  // 複数ジョブを並行して詳細ポーリングする仕組みはまだ無いため、
  // 少なくとも1件は完了/失敗の通知が届くようにする。
  const trackLastSuccessInBackground = useCallback(
    (results: ReceiptJobBatchResultItem[]) => {
      const lastSuccess = [...results].reverse().find((result) => result.success && result.jobId);
      if (!lastSuccess?.jobId) return;

      beginPolling(
        {
          jobId: lastSuccess.jobId,
          clientRequestId: lastSuccess.jobId,
          status: "accepted",
          acceptedAt: new Date().toISOString(),
        },
        new Date().toISOString()
      );
    },
    [beginPolling]
  );

  const handleSubmitReceipt = async () => {
    if (selectedFiles.length === 0) {
      setScreenError("保存する領収書画像を選択してください。");
      return;
    }

    setScreenError(null);
    setBatchResults(null);
    setJob({ status: "uploading" });

    try {
      const filesToUpload = await Promise.all(selectedFiles.map((file) => compressReceiptImage(file)));
      const response = await api.createReceiptJobsBatch({
        files: filesToUpload,
        conversationId,
        messageId,
      });
      setBatchResults(response.results);
      setJob({ status: "idle" });
      trackLastSuccessInBackground(response.results);

      if (response.results.some((result) => result.success)) {
        navigate("/receipt/history", { replace: true });
      }
    } catch (error) {
      logError(error);
      setScreenError(getBackendMessage(error) ?? toUserMessage(error));
      setJob({ status: "idle" });
    }
  };

  const pickImageDisabled =
    job.status === "uploading" || job.status === "accepted" || job.status === "polling";
  const submitReceiptDisabled =
    pickImageDisabled ||
    selectedFiles.length === 0 ||
    job.status === "uploading" ||
    job.status === "accepted" ||
    job.status === "polling";

  return (
    <>
      <div className="rounded-[1.5rem] border border-[#E4D5C8] bg-white px-5 py-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-black text-[#6A4F44]">領収書画像をアップロード</p>
            <p className="mt-2 text-sm leading-6 text-[#7A655C]">
              対応形式: JPEG / PNG / WEBP / HEIC / HEIF
            </p>
            <p className="text-sm leading-6 text-[#7A655C]">10MBまでアップロードできます。</p>
            <p className="text-sm leading-6 text-[#7A655C]">
              最大{RECEIPT_BATCH_MAX_FILES}枚まで、まとめて選択できます。
            </p>
            {selectedFiles.length > 0 ? (
              <p className="mt-2 text-xs font-bold text-[#8E776C]">選択中: {selectedFiles.length}枚</p>
            ) : null}
          </div>

          <button
            type="button"
            onClick={openFilePicker}
            disabled={pickImageDisabled}
            className="inline-flex items-center justify-center rounded-full bg-[#6A4F44] px-5 py-3 text-sm font-black text-white transition hover:bg-[#563E35] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {PICK_IMAGE_BUTTON_LABEL}
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={RECEIPT_ACCEPTED_IMAGE_TYPES.join(",")}
          className="hidden"
          onChange={handleFileSelected}
        />

        {selectedPreviews.length > 0 ? (
          <div className="mt-4 rounded-[1.4rem] border border-[#EADBC8] bg-[#FFF8F4] px-4 py-4">
            <p className="text-sm font-black text-[#6A4F44]">選択した画像（{selectedPreviews.length}枚）</p>
            <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4">
              {selectedPreviews.map((preview) => (
                <div key={preview.url} className="min-w-0">
                  <img
                    src={preview.url}
                    alt={preview.name}
                    className="h-16 w-full rounded-2xl border border-[#E4D5C8] object-cover"
                  />
                  <p className="mt-1 truncate text-[11px] font-bold text-[#8E776C]">{preview.name}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-sm leading-6 text-[#7A655C]">
              内容を確認してから「
              {SUBMIT_RECEIPT_BUTTON_LABEL}
              」を押すと、領収書整理を開始します。
            </p>
          </div>
        ) : null}

        {batchResults ? (
          <div className="mt-4 rounded-[1.4rem] border border-[#EADBC8] bg-white px-4 py-4">
            <p className="text-sm font-black text-[#6A4F44]">アップロード結果</p>
            <ul className="mt-2 space-y-1.5">
              {batchResults.map((result, index) => (
                <li
                  key={`${result.fileName}-${index}`}
                  className={`rounded-xl px-3 py-2 text-xs font-bold ${
                    result.success ? "bg-[#EEF6EC] text-[#4A7C59]" : "bg-[#FEF0EE] text-[#B4533A]"
                  }`}
                >
                  {result.fileName}: {result.success ? "受付済み" : result.error || "失敗しました"}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      {false ? (
        <div className="mt-4 rounded-[1.6rem] border border-[#E4D5C8] bg-white px-5 py-5 shadow-[0_10px_24px_rgba(176,122,85,0.08)]">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[12px] font-black uppercase tracking-[0.16em] text-[#B07A55]">Job Status</p>
              <p className="mt-2 text-lg font-black text-[#6A4F44]">{jobHeadline}</p>
            </div>
            {(job.status === "accepted" || job.status === "polling" || job.status === "timeout") && (
              <button
                type="button"
                onClick={handleRefreshJob}
                disabled={jobRefreshing}
                className="shrink-0 rounded-full border border-[#E4D5C8] bg-white px-4 py-2 text-xs font-bold text-[#6A4F44] transition hover:bg-[#FFF8F1] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {jobRefreshing ? "確認中..." : REFRESH_JOB_STATUS_BUTTON_LABEL}
              </button>
            )}
          </div>

          {(job.status === "uploading" || job.status === "accepted" || job.status === "polling") && (
            <div className="mt-4 rounded-[1.4rem] bg-[#FFF7F1] px-4 py-4 text-sm leading-6 text-[#7A655C]">
              <p>処理が完了するまで、そのままお待ちください。</p>
              {job.acceptedAt ? <p className="mt-2 text-xs">受付時刻: {formatDateTime(job.acceptedAt)}</p> : null}
            </div>
          )}

          {job.status === "completed" && (
            <div className="mt-4 rounded-[1.4rem] border border-[#D8E7D3] bg-[#F7FBF3] px-4 py-4 text-sm leading-6 text-[#58705A]">
              <p>{job.extractedSummary ?? "領収書を処理しました。"}</p>
              {job.processedAt ? <p className="mt-2 text-xs">完了時刻: {formatDateTime(job.processedAt)}</p> : null}
            </div>
          )}

          {job.status === "failed" && (
            <div className="mt-4 rounded-[1.4rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-4 text-sm leading-6 text-[#A1493E]">
              <p>{job.failedError ?? "領収書の処理に失敗しました。"}</p>
              {job.failedStage ? (
                <p className="mt-2 text-xs font-bold">失敗箇所: {formatReceiptStageLabel(job.failedStage)}</p>
              ) : null}
            </div>
          )}

          {job.status === "timeout" && (
            <div className="mt-4 rounded-[1.4rem] border border-[#EADBC8] bg-[#FFF8F4] px-4 py-4 text-sm leading-6 text-[#7A655C]">
              <p>
                閉じても後で再確認できます。しばらくしてから「
                {REFRESH_JOB_STATUS_BUTTON_LABEL}
                」を押してください。
              </p>
            </div>
          )}

          {(job.status === "completed" || job.status === "failed" || job.status === "timeout") && (
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handlePickAnotherFile}
                className="inline-flex items-center justify-center rounded-full bg-[#6A4F44] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#563E35]"
              >
                別の画像を送る
              </button>
            </div>
          )}
        </div>
      ) : null}

      {isJobProcessing ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#EADBC8] bg-[#FFF8F4] px-4 py-3 text-sm leading-6 text-[#7A655C]">
          領収書を処理中です。完了または失敗したら、この画面上部とトップ画面の報告でお知らせします。
        </div>
      ) : null}

      {screenError ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
          {screenError}
        </div>
      ) : null}

      <div className="mt-4 rounded-[1.4rem] border border-white/70 bg-white/80 p-4 text-sm leading-6 text-[#6A4F44]">
        {RECEIPT_GUIDE_LINES.map((line, index) => (
          <p key={index}>{line}</p>
        ))}
      </div>

      <div className="mt-4 rounded-[1.6rem] border border-[#EADBC8] bg-white/70 px-4 py-4 sm:px-5">
        <button
          type="button"
          onClick={() => {
            void handleSubmitReceipt();
          }}
          disabled={submitReceiptDisabled}
          className="w-full rounded-[1.4rem] bg-[#5D4D46] px-4 py-3 text-sm font-black text-[#FDF8F5] shadow-sm transition-all hover:bg-[#4A3E39] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {job.status === "uploading" ? "依頼中..." : SUBMIT_RECEIPT_BUTTON_LABEL}
        </button>
      </div>
    </>
  );
}
